// src/components/layout/Layout.js - Layout corretto con import
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext'; // ✅ Import corretto
import OfflineIndicator from '../offline/OfflineIndicator';
import ChangePasswordModal from '../auth/ChangePasswordModal';
import { 
  ShoppingCart, 
  List, 
  LogOut, 
  User, 
  Menu, 
  X,
  CloudOff,
  Users,
  Shield,
  Key
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout } = useAuth(); // ✅ Usa il hook correttamente
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  const navigation = [
    { name: 'Nuovo Ordine', href: '/', icon: ShoppingCart },
    { name: 'I Miei Ordini', href: '/orders', icon: List },
    { name: 'Gestione Clienti', href: '/customers', icon: Users },
    { name: 'Ordini Offline', href: '/offline-orders', icon: CloudOff },
    ...(user?.isAdmin ? [{ name: 'Amministrazione', href: '/admin', icon: Shield }] : []),
  ];

  const handleLogout = () => {
    if (window.confirm('Sei sicuro di voler uscire?')) {
      logout();
    }
  };

  // ✅ Verifica che user sia disponibile
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-white">
      {/* Mobile menu overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden bg-black bg-opacity-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 flex-shrink-0" style={{backgroundColor: '#fcfbd6'}}>
            <div className="flex items-center space-x-3">
              <img 
                src="/icons/logo_miv.png" 
                alt="Logo" 
                className="h-8 w-8 object-contain"
              />
              <h1 className="text-xl font-bold text-gray-800">Sistema Ordini</h1>
            </div>
            <button
              className="lg:hidden text-gray-800 hover:text-gray-600"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Indicatore offline */}
          <div className="px-4 py-3 border-b border-gray-200">
            <OfflineIndicator />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'text-white shadow-sm'
                      : 'text-gray-600 sidebar-item-hover'
                  }`}
                  style={isActive ? {backgroundColor: '#c48f75'} : {}}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User info */}
          <div className="px-4 py-4 border-t border-gray-200 flex-shrink-0">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{backgroundColor: '#c48f75'}}>
                  <User className="w-5 h-5 text-white" />
                </div>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.name || 'Utente'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.email || 'email@esempio.it'}
                </p>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setShowChangePasswordModal(true)}
                  className="p-2 text-gray-400 rounded-lg transition-colors duration-200 sidebar-item-hover"
                  title="Cambia Password"
                >
                  <Key className="w-4 h-4" />
                </button>
                <button
                  onClick={handleLogout}
                  className="p-2 text-gray-400 rounded-lg transition-colors duration-200 sidebar-item-hover"
                  title="Logout"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile header */}
        <div className="lg:hidden bg-white shadow-sm border-b px-4 py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-gray-600 hover:text-gray-900"
            >
              <Menu className="h-6 w-6" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">Sistema Ordini</h1>
            <div className="w-6 h-6"></div> {/* Spacer for centering */}
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          {children}
        </main>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
    </div>
  );
};

export default Layout;